// Approver details (User Management): who a person is, which combinations they are in, and what
// was routed to them — with, for someone added by e-mail, how far each e-mail and link got. Pure,
// so the wording and grouping are unit-tested (approverDetails.test.js).
import { comboKey, comboLabel } from './routing.js';

/** The person behind a combination entry: a VMS user by id, or an address. */
export const personOf = (entry) =>
  entry?.email ? { email: String(entry.email).toLowerCase() } : { userId: entry?.approverId ?? entry?.approver?.id ?? null };

/** The API query for a person. */
export const activityQuery = (person) => (person.email ? { email: person.email } : { user_id: person.userId });

/** Every combination this person is in, invite first, most specific first. */
export function combosOfPerson(rules, person) {
  const out = [];
  const seen = new Set();
  for (const r of rules || []) {
    const mine = person.email ? String(r.approver_email || '').toLowerCase() === person.email : r.approver_user_id === person.userId && person.userId != null;
    if (!mine) continue;
    const key = `${r.kind}:${comboKey(r.nature_of_business, r.supplier_subcategory)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ kind: r.kind, key, ruleId: r.id, label: comboLabel(r.nature_of_business, r.supplier_subcategory) });
  }
  return out.sort((a, b) => (a.kind === b.kind ? a.ruleId - b.ruleId : a.kind === 'INVITE' ? -1 : 1));
}

/** "Sent", "Delivered", "Opened" … and how it should look. */
export const MAIL_TEXT = {
  unknown: { label: 'Sent before tracking', tone: 'slate', hint: 'Sent before delivery tracking was added, so there is no report for it.' },
  failed: { label: 'Not sent', tone: 'red', hint: 'The mail server refused it.' },
  sent: { label: 'Sent', tone: 'slate', hint: 'Handed to the mail server; no delivery report yet.' },
  delivered: { label: 'Delivered', tone: 'sky', hint: 'Reached their mailbox.' },
  opened: { label: 'Opened', tone: 'green', hint: 'They opened the e-mail.' },
  clicked: { label: 'Opened', tone: 'green', hint: 'They opened the e-mail and clicked in it.' },
  bounced: { label: 'Bounced', tone: 'red', hint: 'Their mail server rejected it — check the address.' },
};

export const LINK_TEXT = {
  not_opened: { label: 'Link not opened yet', tone: 'amber' },
  opened: { label: 'Link opened, not decided', tone: 'sky' },
  used: { label: 'Decided with this link', tone: 'green' },
  expired: { label: 'Link expired', tone: 'red' },
  closed: { label: 'Decided by someone else', tone: 'slate' },
};

/**
 * The e-mail's journey as steps: Sent → Delivered → Opened → Link opened → Decided. A step is
 * done, current (the furthest reached) or not reached; a bounce or failure ends it.
 */
export function deliverySteps(delivery) {
  if (!delivery) return [];
  const { mail, link } = delivery;
  if (mail.status === 'failed' || mail.status === 'bounced') {
    return [{ key: 'mail', label: MAIL_TEXT[mail.status].label, state: 'error', at: mail.at }];
  }
  const mailRank = { unknown: 1, sent: 1, delivered: 2, opened: 3, clicked: 3 }[mail.status] ?? 1;
  const linkOpened = Boolean(link.opened_at) || link.state === 'used';
  // Opening the link proves the e-mail arrived and was opened, even if the provider never said so.
  const reached = link.state === 'used' ? 5 : linkOpened ? 4 : mailRank;
  const steps = [
    { key: 'sent', label: 'Sent', at: link.sent_at },
    { key: 'delivered', label: 'Delivered', at: mail.status === 'delivered' ? mail.at : null },
    { key: 'opened', label: 'E-mail opened', at: mail.status === 'opened' || mail.status === 'clicked' ? mail.at : null },
    { key: 'link', label: 'Link opened', at: link.opened_at },
    { key: 'decided', label: 'Decided', at: link.used_at },
  ];
  return steps.map((s, i) => ({ ...s, state: i + 1 < reached ? 'done' : i + 1 === reached ? 'current' : 'todo' }));
}

/** One line on what happened: "Approved by you, through the e-mail link", "Waiting for a decision", … */
export function outcomeText(item) {
  if (item.status === 'PENDING') return item.assigned ? 'Waiting for a decision' : 'Waiting (not routed to them)';
  const verb = item.status === 'APPROVED' ? 'Approved' : 'Rejected';
  const who = item.decided_by_them ? 'by them' : item.decided_by ? `by ${item.decided_by}` : '';
  const how = item.decided_via === 'link' ? ', through the e-mail link' : item.decided_via === 'vms' ? ' in VMS' : '';
  return `${verb} ${who}${how}`.replace(/\s+/g, ' ').trim();
}

export const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'waiting', label: 'Waiting' },
  { key: 'decided', label: 'Decided' },
];

export function filterItems(items, filter) {
  if (filter === 'waiting') return (items || []).filter((i) => i.status === 'PENDING');
  if (filter === 'decided') return (items || []).filter((i) => i.status !== 'PENDING');
  return items || [];
}

/** "29 Sep 2026, 2:55 pm" in the viewer's own time zone. */
export const fmtWhen = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
